import type { Edge, Node } from '../../../core/models/scene';
import type {
  EdgeAddition,
  ScenarioAggregateContribution,
  ScenarioDelta,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import {
  createEdgeId,
  createEmptyDelta,
  createNodeId,
} from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { FieldSensitivity } from '../../../core/fields/types/FieldManifest';
import { isSensitivityAtLeast } from '../../../core/fields/security/fieldSensitivity';
import { isPersonNodeType, isPositionNodeType, resolveOrgNodeType } from '../fields/entityFieldPacks';
import { orgchartSuccessionFieldPack } from '../fields/packs/succession.pack';
import type {
  SuccessionCandidate,
  SuccessionComputedFields,
  SuccessionReadiness,
  SuccessionRiskLevel,
  SuccessionSummary,
  SuccessionTarget,
} from '../models/SuccessionPlanning';
import {
  DERIVED_SUCCESSOR_EDGE_PREFIX,
  deriveSuccessionLensEdges,
  isOrgSuccessorEdge,
} from './successionLensEdges';
import { assertValidSuccessionPlan } from './SuccessionPlanningValidationService';

const MANAGED_TARGET_FIELDS = new Set([
  'keyPosition',
  'readyNowSuccessor',
  'successorCount',
  'benchStrength',
  'successionRisk',
  'yearsToReadiness',
  'developmentPlan',
  'criticalExperience',
]);

const RISK_ORDER: SuccessionRiskLevel[] = ['low', 'medium', 'high', 'critical'];

/**
 * Role-scoped succession targets. A future role does not have to name a seat:
 * "ready to be an Account Executive — whichever seat opens" is a nomination
 * against the role itself, resolved to a concrete Position at enact time.
 * The ref encodes the exact display title so it round-trips losslessly and
 * matches seats by title until role-profile records are minted at import.
 */
export const SUCCESSION_ROLE_REF_PREFIX = 'role:';

export function successionRoleRef(title: string): string {
  return `${SUCCESSION_ROLE_REF_PREFIX}${encodeURIComponent(title.trim())}`;
}

export function isSuccessionRoleRef(ref: string): boolean {
  return ref.startsWith(SUCCESSION_ROLE_REF_PREFIX);
}

export function successionRoleTitle(ref: string): string {
  try {
    return decodeURIComponent(ref.slice(SUCCESSION_ROLE_REF_PREFIX.length));
  } catch {
    return ref.slice(SUCCESSION_ROLE_REF_PREFIX.length);
  }
}

/**
 * Whether a canvas scenario delta carries any succession content — successor
 * edges or managed succession fields. A scenario holding only structural edits
 * (e.g. a node deletion) is owned by the general scenario banner; rendering the
 * succession draft bar for it offers an Apply that is disabled by construction.
 */
export function scenarioDeltaHasSuccessionContent(delta: ScenarioDelta | undefined): boolean {
  if (!delta) return false;
  return delta.updates.some((update) => MANAGED_TARGET_FIELDS.has(update.change.field))
    || delta.edgeAdditions.some((edge) =>
      isOrgSuccessorEdge('org', { id: edge.id, type: edge.type, from: edge.from, to: edge.to, attrs: edge.props ?? {} } as Edge))
    || delta.edgeRemovals.some((edge) => {
      const edgeId = String(edge.edgeId);
      return edgeId.startsWith(DERIVED_SUCCESSOR_EDGE_PREFIX)
        || edgeId.startsWith('server-succession:')
        || edgeId.startsWith('optimistic-succession:');
    });
}

const PROTECTED_QUALIFIER_MINIMUM_SENSITIVITY: FieldSensitivity = 'hr_restricted';

/**
 * Succession field ids whose declared field-pack sensitivity is hr_restricted
 * or above. Canvas-only scenario contributions live in the shared Y.Doc, which
 * has no per-field ACL, so values for these fields must never enter that write
 * path. Driven by the succession field-pack sensitivity declarations rather
 * than a hard-coded name list. The succession pack is the declaration
 * authority for slate qualifiers; other packs reuse ids like `readiness` for
 * unrelated projection-owned node scalars at different tiers.
 */
const PROTECTED_SUCCESSION_FIELD_IDS: ReadonlySet<string> = (() => {
  const ids = new Set<string>();
  for (const field of orgchartSuccessionFieldPack.fields) {
    if (field.sensitivity
      && isSensitivityAtLeast(field.sensitivity, PROTECTED_QUALIFIER_MINIMUM_SENSITIVITY)) {
      ids.add(field.id);
    }
  }
  return ids;
})();

/** True when the field's declared sensitivity bars it from shared-doc drafts. */
export function isProtectedSuccessionField(fieldId: string): boolean {
  return PROTECTED_SUCCESSION_FIELD_IDS.has(fieldId);
}

function readNodeValue(node: Node | undefined, field: string): unknown {
  if (!node) return undefined;
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const props = (node.props ?? {}) as Record<string, unknown>;
  if (attrs[field] !== undefined) return attrs[field];
  if (props[field] !== undefined) return props[field];
  for (const namespace of ['succession', 'talent', 'workforce']) {
    const attrNamespace = attrs[namespace];
    if (attrNamespace && typeof attrNamespace === 'object' && !Array.isArray(attrNamespace)) {
      const value = (attrNamespace as Record<string, unknown>)[field];
      if (value !== undefined) return value;
    }
    const propNamespace = props[namespace];
    if (propNamespace && typeof propNamespace === 'object' && !Array.isArray(propNamespace)) {
      const value = (propNamespace as Record<string, unknown>)[field];
      if (value !== undefined) return value;
    }
  }
  return (node as unknown as Record<string, unknown>)[field];
}

function nodeName(node: Node | undefined): string {
  return String(readNodeValue(node, 'name') ?? node?.name ?? node?.text ?? '').trim();
}

function relation(edge: Edge): string {
  return String((edge.attrs as Record<string, unknown> | undefined)?.relation ?? edge.type ?? '');
}

function isOccupiesEdge(edge: Edge): boolean {
  return String(edge.type) === 'occupies' || relation(edge) === 'occupies';
}

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  return String(value ?? '').trim().toLowerCase() === 'true';
}

function asRank(value: unknown): number | undefined {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : undefined;
}

export function normalizeSuccessionReadiness(value: unknown): SuccessionReadiness {
  switch (String(value ?? '').trim().toLowerCase()) {
    case 'ready_now':
      return 'ready_now';
    case 'ready_1_2':
    case 'ready_1_2_years':
      return 'ready_1_2_years';
    case 'ready_3_5':
    case 'ready_3_plus':
      return 'ready_3_plus';
    default:
      return 'unassessed';
  }
}

function canonicalCandidate(edge: Edge): SuccessionCandidate {
  const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
  const legacyEmergency = String(attrs.readiness ?? '').toLowerCase() === 'emergency';
  // Projected server edges carry the stored nomination's identity in
  // relationExternalId; prefer it over the synthetic canvas edge id so
  // downstream intents (enact) reference something the server can resolve.
  const relationExternalId = typeof attrs.relationExternalId === 'string'
    ? attrs.relationExternalId.trim()
    : '';
  return {
    edgeId: relationExternalId || edge.id,
    personId: edge.from,
    targetId: edge.to,
    readiness: legacyEmergency ? 'ready_now' : normalizeSuccessionReadiness(attrs.readiness),
    rank: asRank(attrs.rank),
    emergencyCover: legacyEmergency || asBoolean(attrs.emergencyCover),
    developmentPlan: typeof attrs.developmentPlan === 'string' ? attrs.developmentPlan : undefined,
    criticalExperience: typeof attrs.criticalExperience === 'string' ? attrs.criticalExperience : undefined,
    assessedAt: typeof attrs.assessedAt === 'string' ? attrs.assessedAt : undefined,
    assessedBy: typeof attrs.assessedBy === 'string' ? attrs.assessedBy : undefined,
    source: edge.id.startsWith(DERIVED_SUCCESSOR_EDGE_PREFIX) ? 'legacy' : 'relationship',
  };
}

function candidateSort(nodesById: Map<string, Node>) {
  return (left: SuccessionCandidate, right: SuccessionCandidate): number => {
    const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER;
    const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) return leftRank - rightRank;
    const leftName = nodeName(nodesById.get(left.personId));
    const rightName = nodeName(nodesById.get(right.personId));
    return leftName.localeCompare(rightName);
  };
}

function applyRelevantNodeUpdates(nodes: Node[], delta?: ScenarioDelta): Node[] {
  if (!delta || delta.updates.length === 0) return nodes;
  const updates = new Map<string, Array<{ field: string; newValue: unknown }>>();
  for (const update of delta.updates) {
    const current = updates.get(update.nodeId as string) ?? [];
    current.push({ field: update.change.field, newValue: update.change.newValue });
    updates.set(update.nodeId as string, current);
  }
  return nodes.map((node) => {
    const nodeUpdates = updates.get(node.id);
    if (!nodeUpdates) return node;
    const attrs = { ...((node.attrs ?? {}) as Record<string, unknown>) };
    const props = { ...((node.props ?? {}) as Record<string, unknown>) };
    for (const update of nodeUpdates) {
      attrs[update.field] = update.newValue;
      props[update.field] = update.newValue;
    }
    return { ...node, attrs, props };
  });
}

function additionToNode(addition: ScenarioDelta['additions'][number]): Node {
  return {
    id: addition.id as string,
    type: addition.type,
    name: addition.name,
    x: addition.position?.x ?? 0,
    y: addition.position?.y ?? 0,
    attrs: {
      ...(addition.props ?? {}),
      ...(addition.title ? { title: addition.title } : {}),
      ...(addition.department ? { department: addition.department } : {}),
    },
    props: { ...(addition.props ?? {}) },
  };
}

function additionToEdge(addition: EdgeAddition): Edge {
  return {
    id: addition.id as string,
    type: addition.type as Edge['type'],
    from: addition.from as string,
    to: addition.to as string,
    attrs: { ...(addition.props ?? {}) },
  };
}

function retargetDerivedRoleCardEdges(derived: Edge[], graphEdges: Edge[]): Edge[] {
  const occupiedPositionByPerson = new Map(
    graphEdges.filter(isOccupiesEdge).map((edge) => [edge.from, edge.to] as const),
  );
  const explicitPairs = new Set(
    graphEdges
      .filter((edge) => isOrgSuccessorEdge('org', edge))
      .map((edge) => `${edge.from}\u0000${edge.to}`),
  );
  return derived.flatMap((edge) => {
    const targetId = occupiedPositionByPerson.get(edge.to) ?? edge.to;
    const pair = `${edge.from}\u0000${targetId}`;
    if (explicitPairs.has(pair)) return [];
    explicitPairs.add(pair);
    if (targetId === edge.to) return [edge];
    return [{
      ...edge,
      id: `${edge.id}:position:${encodeURIComponent(targetId)}`,
      to: targetId,
      attrs: { ...edge.attrs, derivedFromNodeId: edge.to },
    }];
  });
}

export function projectSuccessionGraph(
  nodes: Node[],
  edges: Edge[],
  delta?: ScenarioDelta,
): { nodes: Node[]; edges: Edge[] } {
  const removedNodeIds = new Set((delta?.removals ?? []).map((entry) => entry.nodeId as string));
  const nodesWithAdditions = delta
    ? [
        ...nodes.filter((node) => !removedNodeIds.has(node.id)),
        ...delta.additions
          .filter((addition) => !removedNodeIds.has(addition.id as string))
          .filter((addition) => !nodes.some((node) => node.id === addition.id as string))
          .map(additionToNode),
      ]
    : nodes;
  const projectedNodes = applyRelevantNodeUpdates(nodesWithAdditions, delta);
  if (!delta) {
    const derived = deriveSuccessionLensEdges({
      diagramType: 'org',
      nodes: projectedNodes,
      edges,
      lensActive: true,
    });
    return { nodes: projectedNodes, edges: [...edges, ...retargetDerivedRoleCardEdges(derived, edges)] };
  }

  const removedIds = new Set(delta.edgeRemovals.map((entry) => entry.edgeId as string));
  const projectedEdges = edges.filter((edge) => !removedIds.has(edge.id));
  for (const addition of delta.edgeAdditions) {
    if (removedIds.has(addition.id as string)) continue;
    projectedEdges.push(additionToEdge(addition));
  }
  const derived = deriveSuccessionLensEdges({
    diagramType: 'org',
    nodes: projectedNodes,
    edges: projectedEdges,
    lensActive: true,
  });
  return {
    nodes: projectedNodes,
    edges: [...projectedEdges, ...retargetDerivedRoleCardEdges(derived, projectedEdges)],
  };
}

export function resolveSuccessionTarget(
  selectedNodeId: string,
  nodes: Node[],
  edges: Edge[],
): SuccessionTarget {
  const selected = nodes.find((node) => node.id === selectedNodeId);
  if (selected && isPositionNodeType(resolveOrgNodeType(selected))) {
    return { selectedNodeId, targetId: selectedNodeId, kind: 'position' };
  }
  const occupancy = edges.find((edge) => edge.from === selectedNodeId && isOccupiesEdge(edge));
  if (occupancy && nodes.some((node) => node.id === occupancy.to)) {
    return { selectedNodeId, targetId: occupancy.to, kind: 'position' };
  }
  return { selectedNodeId, targetId: selectedNodeId, kind: 'normalization_required' };
}

export interface SuccessionPlanningIndex {
  nodesById: Map<string, Node>;
  positionByOccupantId: Map<string, string>;
  incumbentByPositionId: Map<string, string>;
  successorEdgesByPositionId: Map<string, Edge[]>;
}

/** Build the graph indexes once for all succession summaries. */
export function buildSuccessionPlanningIndex(nodes: Node[], edges: Edge[]): SuccessionPlanningIndex {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const positionByOccupantId = new Map<string, string>();
  const incumbentByPositionId = new Map<string, string>();
  const successorEdgesByPositionId = new Map<string, Edge[]>();
  for (const edge of edges) {
    if (isOccupiesEdge(edge)) {
      positionByOccupantId.set(edge.from, edge.to);
      incumbentByPositionId.set(edge.to, edge.from);
    }
    if (isOrgSuccessorEdge('org', edge)) {
      const current = successorEdgesByPositionId.get(edge.to) ?? [];
      current.push(edge);
      successorEdgesByPositionId.set(edge.to, current);
    }
  }
  return { nodesById, positionByOccupantId, incumbentByPositionId, successorEdgesByPositionId };
}

function resolveSuccessionTargetFromIndex(
  selectedNodeId: string,
  index: SuccessionPlanningIndex,
): SuccessionTarget {
  const selected = index.nodesById.get(selectedNodeId);
  if (selected && isPositionNodeType(resolveOrgNodeType(selected))) {
    return { selectedNodeId, targetId: selectedNodeId, kind: 'position' };
  }
  const positionId = index.positionByOccupantId.get(selectedNodeId);
  if (positionId && index.nodesById.has(positionId)) {
    return { selectedNodeId, targetId: positionId, kind: 'position' };
  }
  return { selectedNodeId, targetId: selectedNodeId, kind: 'normalization_required' };
}

function nextRiskLevel(level: SuccessionRiskLevel): SuccessionRiskLevel {
  return RISK_ORDER[Math.min(RISK_ORDER.indexOf(level) + 1, RISK_ORDER.length - 1)];
}

function dateWithinOneYear(value: unknown, now: Date): boolean {
  if (typeof value !== 'string' || value.trim() === '') return false;
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return false;
  const remaining = time - now.getTime();
  return remaining >= 0 && remaining <= 365 * 24 * 60 * 60 * 1000;
}

export function buildSuccessionSummary(
  selectedNodeId: string,
  nodes: Node[],
  edges: Edge[],
  now = new Date(),
  existingIndex?: SuccessionPlanningIndex,
): SuccessionSummary {
  const index = existingIndex ?? buildSuccessionPlanningIndex(nodes, edges);
  const target = resolveSuccessionTargetFromIndex(selectedNodeId, index);
  const { nodesById } = index;
  const unique = new Map<string, SuccessionCandidate>();
  for (const edge of index.successorEdgesByPositionId.get(target.targetId) ?? []) {
    const candidate = canonicalCandidate(edge);
    const current = unique.get(candidate.personId);
    if (!current || (current.source === 'legacy' && candidate.source === 'relationship')) {
      unique.set(candidate.personId, candidate);
    }
  }
  const candidates = Array.from(unique.values()).sort(candidateSort(nodesById));
  const targetNode = nodesById.get(target.targetId);
  const incumbent = target.kind === 'position'
    ? nodesById.get(index.incumbentByPositionId.get(target.targetId) ?? '')
    : nodesById.get(selectedNodeId);
  const keyPosition = asBoolean(readNodeValue(targetNode, 'keyPosition'))
    || asBoolean(readNodeValue(incumbent, 'keyPosition'));
  const impact = String(readNodeValue(targetNode, 'impactOfLoss')
    ?? readNodeValue(incumbent, 'impactOfLoss')
    ?? '').toLowerCase();
  const flightRisk = String(readNodeValue(incumbent, 'flightRisk') ?? '').toLowerCase();
  const imminentExit = dateWithinOneYear(
    readNodeValue(incumbent, 'departureDate') ?? readNodeValue(incumbent, 'retirementDate'),
    now,
  );
  const highImpact = keyPosition || impact === 'critical' || impact === 'high';
  const coverage = summarizeSuccessionCoverage(candidates, {
    keyPosition,
    highImpact,
    flightRiskHigh: flightRisk === 'high',
    imminentExit,
  });

  return {
    target,
    candidates,
    ...coverage,
    keyPosition,
  };
}

export function summarizeSuccessionCoverage(
  candidates: readonly SuccessionCandidate[],
  context: {
    readonly keyPosition: boolean;
    readonly highImpact: boolean;
    readonly flightRiskHigh: boolean;
    readonly imminentExit: boolean;
    /**
     * Role-pool candidates whose future role matches this seat's title but who
     * are not named against the seat. A key position must have a NAMED
     * successor — the pool never clears its risk — while other seats may be
     * measured on the bench the pool provides.
     */
    readonly rolePoolCount?: number;
  },
): Pick<
  SuccessionSummary,
  'successorCount' | 'readyNowCandidateIds' | 'benchStrength' | 'coverageRisk' | 'successionRisk' | 'riskReasons'
> {
  const viable = candidates.filter((candidate) =>
    candidate.readiness === 'ready_now' || candidate.readiness === 'ready_1_2_years');
  const readyNow = candidates.filter((candidate) => candidate.readiness === 'ready_now');
  const rolePoolCount = context.rolePoolCount ?? 0;
  const poolCounts = !context.keyPosition && rolePoolCount > 0;
  let benchStrength = candidates.length === 0
    ? 'none' as const
    : viable.length === 0
      ? 'weak' as const
      : viable.length >= 2 && readyNow.length >= 1
        ? 'strong' as const
        : 'adequate' as const;
  let coverageRisk: SuccessionRiskLevel = candidates.length === 0
    ? 'critical'
    : viable.length === 0
      ? 'high'
      : readyNow.length === 0 || viable.length === 1
        ? 'medium'
        : 'low';
  if (poolCounts) {
    if (benchStrength === 'none') benchStrength = rolePoolCount >= 2 ? 'adequate' : 'weak';
    else if (benchStrength === 'weak') benchStrength = 'adequate';
    const floor: SuccessionRiskLevel = rolePoolCount >= 2 ? 'medium' : 'high';
    if (RISK_ORDER.indexOf(coverageRisk) > RISK_ORDER.indexOf(floor)) coverageRisk = floor;
  }
  const highExposure = context.flightRiskHigh || context.imminentExit;
  const successionRisk = context.highImpact && highExposure && coverageRisk !== 'critical'
    ? nextRiskLevel(coverageRisk)
    : coverageRisk;
  const riskReasons: string[] = [];
  if (candidates.length === 0) riskReasons.push('No named successor');
  else if (viable.length === 0) riskReasons.push('No successor ready within two years');
  else if (readyNow.length === 0) riskReasons.push('No ready-now successor');
  else if (viable.length === 1) riskReasons.push('Only one viable successor');
  else riskReasons.push('Ready-now coverage with an additional viable candidate');
  if (poolCounts) {
    riskReasons.push(`Role pool covers this seat (${rolePoolCount} candidate${rolePoolCount === 1 ? '' : 's'})`);
  }
  if (context.keyPosition && rolePoolCount > 0 && candidates.length === 0) {
    riskReasons.push('Role-pool candidates exist, but a key position needs a named successor');
  }
  if (context.keyPosition) riskReasons.push('Key position');
  if (context.flightRiskHigh) riskReasons.push('Incumbent flight risk is high');
  if (context.imminentExit) riskReasons.push('Incumbent exit is expected within one year');
  return {
    successorCount: candidates.length,
    readyNowCandidateIds: readyNow.map((candidate) => candidate.personId),
    benchStrength,
    coverageRisk,
    successionRisk,
    riskReasons,
  };
}

export function buildSuccessionComputedFieldMap(
  nodes: Node[],
  edges: Edge[],
  delta?: ScenarioDelta,
): Map<string, SuccessionComputedFields> {
  const projected = projectSuccessionGraph(nodes, edges, delta);
  const index = buildSuccessionPlanningIndex(projected.nodes, projected.edges);
  const { nodesById } = index;
  const asOf = new Date();
  const result = new Map<string, SuccessionComputedFields>();
  for (const node of projected.nodes) {
    const nodeType = resolveOrgNodeType(node);
    if (!isPositionNodeType(nodeType) && !isPersonNodeType(nodeType)) continue;
    const summary = buildSuccessionSummary(node.id, projected.nodes, projected.edges, asOf, index);
    const readyNames = summary.readyNowCandidateIds
      .map((id) => nodeName(nodesById.get(id)))
      .filter(Boolean);
    result.set(node.id, {
      successorCount: summary.successorCount,
      readyNowSuccessor: readyNames[0] ?? '',
      readyNowSuccessors: readyNames,
      benchStrength: summary.benchStrength,
      successionRisk: summary.successionRisk,
      successionRiskReason: summary.riskReasons.join(' • '),
    });
  }
  return result;
}

function candidateProps(
  candidate: SuccessionCandidate,
  assessedAt?: string,
  assessedBy?: string,
  scrubProtectedQualifiers = false,
): Record<string, unknown> {
  const props: Record<string, unknown> = {
    relation: 'successor_for',
    predicate: 'successor_of',
    ...(candidate.readiness === 'unassessed' ? {} : { readiness: candidate.readiness }),
    ...(candidate.rank ? { rank: candidate.rank } : {}),
    emergencyCover: candidate.emergencyCover,
    ...(candidate.developmentPlan ? { developmentPlan: candidate.developmentPlan } : {}),
    ...(candidate.criticalExperience ? { criticalExperience: candidate.criticalExperience } : {}),
    ...(assessedAt || candidate.assessedAt ? { assessedAt: assessedAt ?? candidate.assessedAt } : {}),
    ...(candidate.assessedBy || assessedBy ? { assessedBy: candidate.assessedBy ?? assessedBy } : {}),
    sensitivityTier: 'hr_restricted',
  };
  if (!scrubProtectedQualifiers) return props;
  return Object.fromEntries(
    Object.entries(props).filter(([key]) => !isProtectedSuccessionField(key)),
  );
}

function candidatesEqual(edge: Edge, candidate: SuccessionCandidate): boolean {
  const current = canonicalCandidate(edge);
  return current.personId === candidate.personId
    && current.readiness === candidate.readiness
    && current.rank === candidate.rank
    && current.emergencyCover === candidate.emergencyCover
    && current.developmentPlan === candidate.developmentPlan
    && current.criticalExperience === candidate.criticalExperience;
}

function legacyTargetUpdate(
  node: Node | undefined,
  field: string,
  newValue: unknown,
) {
  const oldValue = readNodeValue(node, field);
  if (oldValue === newValue || (oldValue == null && newValue == null)) return null;
  return {
    nodeId: createNodeId(node?.id ?? ''),
    change: { field, oldValue, newValue },
  };
}

export function buildSuccessionScenarioDelta(options: {
  baseNodes: Node[];
  baseEdges: Edge[];
  currentDelta: ScenarioDelta;
  selectedNodeId: string;
  candidates: SuccessionCandidate[];
  keyPosition: boolean;
  assessedAt?: string;
  assessedBy?: string;
  /**
   * Drop hr_restricted-tier qualifier values from the produced delta. Required
   * whenever the delta is written into the shared Y.Doc (canvas-only drafts).
   */
  scrubProtectedQualifiers?: boolean;
}): ScenarioDelta {
  const { baseNodes, baseEdges, currentDelta, selectedNodeId } = options;
  const scrubProtectedQualifiers = options.scrubProtectedQualifiers ?? false;
  const target = resolveSuccessionTarget(selectedNodeId, baseNodes, baseEdges);
  const validation = assertValidSuccessionPlan({
    selectedNodeId,
    target,
    candidates: options.candidates,
    nodes: baseNodes,
    edges: baseEdges,
  });
  const baseTargetEdges = baseEdges.filter((edge) =>
    edge.to === validation.target.targetId && isOrgSuccessorEdge('org', edge));
  const baseTargetEdgeIds = new Set(baseTargetEdges.map((edge) => edge.id));
  const managedNodeIds = new Set([validation.target.targetId, selectedNodeId]);
  const targetNode = baseNodes.find((node) => node.id === validation.target.targetId);
  const selectedNode = baseNodes.find((node) => node.id === selectedNodeId);

  const next: ScenarioDelta = {
    ...currentDelta,
    edgeAdditions: currentDelta.edgeAdditions.filter((edge) =>
      !(edge.to === validation.target.targetId && (edge.type === 'successor_for'
        || (edge.props as Record<string, unknown> | undefined)?.relation === 'successor_for'))),
    edgeRemovals: currentDelta.edgeRemovals.filter((edge) => !baseTargetEdgeIds.has(edge.edgeId as string)),
    updates: currentDelta.updates.filter((update) =>
      !managedNodeIds.has(update.nodeId as string) || !MANAGED_TARGET_FIELDS.has(update.change.field)),
  };

  const desiredByPerson = new Map(validation.candidates.map((candidate) => [candidate.personId, candidate]));
  for (const edge of baseTargetEdges) {
    const desired = desiredByPerson.get(edge.from);
    if (desired && candidatesEqual(edge, desired)) {
      desiredByPerson.delete(edge.from);
      continue;
    }
    next.edgeRemovals.push({ edgeId: createEdgeId(edge.id) });
  }

  for (const candidate of desiredByPerson.values()) {
    const props = candidateProps(candidate, options.assessedAt, options.assessedBy, scrubProtectedQualifiers);
    next.edgeAdditions.push({
      id: createEdgeId(`successor-for:${encodeURIComponent(validation.target.targetId)}:${encodeURIComponent(candidate.personId)}`),
      type: 'successor_for',
      from: createNodeId(candidate.personId),
      to: createNodeId(validation.target.targetId),
      props,
    });
  }

  const updates = [
    legacyTargetUpdate(targetNode, 'keyPosition', options.keyPosition),
    legacyTargetUpdate(targetNode, 'readyNowSuccessor', null),
    legacyTargetUpdate(targetNode, 'successorCount', null),
    legacyTargetUpdate(targetNode, 'benchStrength', null),
    legacyTargetUpdate(targetNode, 'successionRisk', null),
    legacyTargetUpdate(targetNode, 'yearsToReadiness', null),
    legacyTargetUpdate(targetNode, 'developmentPlan', null),
    legacyTargetUpdate(targetNode, 'criticalExperience', null),
    ...(validation.target.targetId !== selectedNodeId ? [
      legacyTargetUpdate(selectedNode, 'keyPosition', null),
      legacyTargetUpdate(selectedNode, 'readyNowSuccessor', null),
      legacyTargetUpdate(selectedNode, 'successorCount', null),
      legacyTargetUpdate(selectedNode, 'benchStrength', null),
      legacyTargetUpdate(selectedNode, 'successionRisk', null),
      legacyTargetUpdate(selectedNode, 'yearsToReadiness', null),
      legacyTargetUpdate(selectedNode, 'developmentPlan', null),
      legacyTargetUpdate(selectedNode, 'criticalExperience', null),
    ] : []),
  ].filter((update): update is NonNullable<typeof update> => update !== null)
    // Null clears of legacy fields carry no content; protected-tier values do.
    .filter((update) => !scrubProtectedQualifiers
      || update.change.newValue == null
      || !isProtectedSuccessionField(update.change.field));
  next.updates.push(...updates);
  return next;
}

export const successionAggregateKey = (positionId: string): string =>
  `orgchart.succession:${positionId}`;

/**
 * Build the independently convergent fragment for one Position's successor
 * slate. The legacy Scenario.delta remains the base read model.
 *
 * The contribution is written verbatim into the shared Y.Doc
 * (`scenario-contributions:<scenarioId>`), which any collaborator with doc
 * read access can inspect, so hr_restricted qualifier values are always
 * scrubbed here. Server-backed drafts bypass this path entirely and keep
 * protected values in isolated server storage.
 */
export function buildSuccessionAggregateContribution(options: {
  baseNodes: Node[];
  baseEdges: Edge[];
  selectedNodeId: string;
  candidates: SuccessionCandidate[];
  keyPosition: boolean;
  assessedAt: string;
  assessedBy: string;
}): ScenarioAggregateContribution {
  const target = resolveSuccessionTarget(options.selectedNodeId, options.baseNodes, options.baseEdges);
  const delta = buildSuccessionScenarioDelta({
    ...options,
    currentDelta: createEmptyDelta(),
    scrubProtectedQualifiers: true,
  });
  const targetEdgeIds = options.baseEdges
    .filter((edge) => edge.to === target.targetId && isOrgSuccessorEdge('org', edge))
    .map((edge) => createEdgeId(edge.id));
  const managedFields = Array.from(MANAGED_TARGET_FIELDS);

  return {
    aggregateKey: successionAggregateKey(target.targetId),
    delta,
    ownership: {
      nodeFields: {
        [target.targetId]: managedFields,
        [options.selectedNodeId]: managedFields,
      },
      edgeIds: [
        ...targetEdgeIds,
        ...delta.edgeAdditions.map((edge) => edge.id),
      ],
      edgeTargets: [{
        targetId: createNodeId(target.targetId),
        relationTypes: ['successor_for'],
      }],
    },
    updatedAt: options.assessedAt,
    updatedBy: options.assessedBy,
  };
}

export function futureRoleCandidates(
  personId: string,
  nodes: Node[],
  edges: Edge[],
): SuccessionCandidate[] {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  return edges
    .filter((edge) => edge.from === personId && isOrgSuccessorEdge('org', edge))
    .map(canonicalCandidate)
    .sort((left, right) => nodeName(nodesById.get(left.targetId)).localeCompare(nodeName(nodesById.get(right.targetId))));
}
